import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Upload, Loader2, Database } from "lucide-react";

/**
 * Historical Data Sync card — admin-only.
 *
 * Copies league history (users, matches, predictions, seasons, season_closures,
 * pool_config) between environments via a downloadable JSON snapshot.
 *
 * Typical workflow:
 *   1. On PREVIEW: click "Download snapshot" → get preview_snapshot.json.
 *   2. On PRODUCTION: click "Restore from file" → pick that JSON → confirm.
 *
 * All uploads pass through the admin-gated /api/admin/import endpoint which
 * upserts by natural key. Existing users' password_hash / role / editor /
 * client_id / pub_id are preserved.
 */
export default function HistoricalDataSyncCard() {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastImport, setLastImport] = useState(null);
  const fileInputRef = useRef(null);

  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      a.href = url;
      a.download = `dugsy-snapshot-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(
        `Snapshot downloaded (${data.users?.length || 0} users, ${
          data.matches?.length || 0
        } matches, ${data.season_closures?.length || 0} closures).`,
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const pickFile = () => fileInputRef.current?.click();

  const onFilePicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking same file
    if (!f) return;
    try {
      const text = await f.text();
      const parsed = JSON.parse(text);
      setFile({ name: f.name, size: f.size, payload: parsed });
      setPreview({
        users: parsed.users?.length || 0,
        matches: parsed.matches?.length || 0,
        predictions: parsed.predictions?.length || 0,
        seasons: parsed.seasons?.length || 0,
        season_closures: parsed.season_closures?.length || 0,
        pool_config: parsed.pool_config?.length || 0,
      });
      setLastImport(null);
      setConfirmOpen(true);
    } catch (err) {
      toast.error("That file isn't valid JSON.");
    }
  };

  const executeImport = async () => {
    if (!file?.payload) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import", file.payload);
      setLastImport(data?.imported || {});
      toast.success("Snapshot restored.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const rows = [
    ["Users", "users"],
    ["Matches", "matches"],
    ["Predictions", "predictions"],
    ["Seasons", "seasons"],
    ["Season closures (winners / final standings)", "season_closures"],
    ["Pool config", "pool_config"],
  ];

  return (
    <section
      data-testid="data-sync-card"
      className="border border-zinc-200 rounded-sm bg-white p-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-2">
            <Database className="h-3.5 w-3.5 text-blue-700" /> Historical data sync
          </p>
          <h2 className="font-heading text-2xl font-semibold text-zinc-950 mt-1">
            Copy league history across environments.
          </h2>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Download a JSON snapshot of users, matches, predictions, seasons,
            closures and pool config from this environment — then restore it on
            another (e.g. preview → production). Existing accounts keep their
            password, role and pub assignment; only missing rows are added.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={download}
            disabled={busy}
            data-testid="data-sync-download-btn"
            className="rounded-sm"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Download snapshot
          </Button>
          <Button
            type="button"
            onClick={pickFile}
            disabled={busy}
            data-testid="data-sync-restore-btn"
            className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white"
          >
            <Upload className="h-4 w-4 mr-2" />
            Restore from file
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={onFilePicked}
            data-testid="data-sync-file-input"
          />
        </div>
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(v) => {
          if (!busy) setConfirmOpen(v);
          if (!v) { setFile(null); setPreview(null); setLastImport(null); }
        }}
      >
        <DialogContent
          data-testid="data-sync-dialog"
          className="rounded-sm max-w-lg"
        >
          <DialogHeader>
            <DialogTitle>
              Restore snapshot to this environment?
            </DialogTitle>
            <DialogDescription>
              {file?.name
                ? `Loaded from ${file.name}. Rows will be upserted (existing rows updated, new rows inserted).`
                : "Loading snapshot…"}
            </DialogDescription>
          </DialogHeader>

          {preview && !lastImport && (
            <ul
              className="text-sm text-zinc-800 divide-y divide-zinc-200 border border-zinc-200 rounded-sm"
              data-testid="data-sync-preview-list"
            >
              {rows.map(([label, key]) => (
                <li key={key} className="flex items-center justify-between px-3 py-2">
                  <span className="text-zinc-700">{label}</span>
                  <span
                    className="font-mono tabular-nums"
                    data-testid={`data-sync-preview-${key}`}
                  >
                    {Number(preview[key] || 0)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {lastImport && (
            <div
              className="text-sm text-zinc-800 border border-emerald-200 bg-emerald-50 rounded-sm p-3"
              data-testid="data-sync-result"
            >
              <p className="font-semibold text-emerald-800 mb-2">
                Import complete.
              </p>
              <ul className="space-y-1">
                {rows.map(([label, key]) => (
                  <li
                    key={key}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="text-zinc-700">{label}</span>
                    <span
                      className="font-mono tabular-nums"
                      data-testid={`data-sync-result-${key}`}
                    >
                      {Number(lastImport[key] || 0)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
              data-testid="data-sync-cancel-btn"
              className="rounded-sm"
            >
              {lastImport ? "Close" : "Cancel"}
            </Button>
            {!lastImport && (
              <Button
                type="button"
                onClick={executeImport}
                disabled={busy || !preview}
                data-testid="data-sync-confirm-btn"
                className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white"
              >
                {busy ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Restoring…
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Yes, restore snapshot
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
